import { useState, useEffect, useRef } from "react"
import { DirectQuery, DirectType, getDirects } from "../direct"
import axios from "axios"

export interface IFetchDirect {
  directsData: DirectType[]
  loading: boolean
  error: { status: number } | null
  refresh: (resetIfNull?: boolean) => Promise<void>
  nResults: number
}

export const useDirectAPI = (
  query: DirectQuery,
  abortOnChange = true
): IFetchDirect => {
  const [directsData, setDirects] = useState<DirectType[]>([])
  const [nResults, setNResults] = useState(0)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<{ status: number } | null>(null)
  const lastQueryId = useRef(0)

  const fetchDirects = async (queryParam: DirectQuery, resetIfNull = false) => {
    lastQueryId.current += 1
    const queryId = lastQueryId.current
    setLoading(true)
    try {
      const directData = await getDirects(queryParam)
      if (abortOnChange && queryId !== lastQueryId.current) return
      setError(null)
      setDirects(directData.directs)
      setNResults(directData.nResults)
    } catch (err: unknown) {
      if (abortOnChange && queryId !== lastQueryId.current) return
      if (axios.isAxiosError(err)) {
        setError({ status: err.status || 500 })
      } else {
        setError({ status: 500 })
      }
      if (resetIfNull) setDirects([])
    } finally {
      if (!(abortOnChange && queryId !== lastQueryId.current)) setLoading(false)
    }
  }

  const refresh = async (resetIfNull = false) => {
    await fetchDirects(query, resetIfNull)
  }

  useEffect(() => {
    fetchDirects(query)
  }, [query.search, query.id])
  return { directsData, loading, error, refresh, nResults }
}
